import { useState } from 'react';
import { ExternalLink, Loader2, Sparkles, Key, CheckCircle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { auth } from '@/lib/firebase';
import { byocApi } from '@/lib/api/byoc';
import {
  initiateAnthropicOAuth,
  getStoredPkceValues,
  clearStoredPkceValues,
  parseAuthCode,
} from '@/lib/anthropic-oauth';

type AuthMethod = 'select' | 'claude-pro-max' | 'manual-api-key';

interface ByocConnectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnect: (apiKey: string) => Promise<void>;
  onSuccess?: () => void; // Called after any successful connection (manual or OAuth)
}

export function ByocConnectModal({
  open,
  onOpenChange,
  onConnect,
  onSuccess,
}: ByocConnectModalProps) {
  const [authMethod, setAuthMethod] = useState<AuthMethod>('select');
  const [apiKey, setApiKey] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await onConnect(apiKey);
      setIsLoading(false);
      setIsSuccess(true);
      onSuccess?.(); // Notify parent to refresh status
      // Show success briefly then close
      setTimeout(() => {
        resetAndClose();
      }, 1200);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError?.response?.data?.message || 'Failed to connect');
      setIsLoading(false);
    }
  };

  const handleOpenOAuthPage = async () => {
    try {
      setError(null);
      const url = await initiateAnthropicOAuth();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError('Failed to initiate OAuth flow');
    }
  };

  const handleOAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const pkceValues = getStoredPkceValues();
      if (!pkceValues) {
        throw new Error('Session expired. Please click the authorization link again.');
      }

      const token = await auth.getIdToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Parse the auth code - OpenCode splits by #
      const { code, state } = parseAuthCode(authCode.trim());

      await byocApi.exchangeAnthropicCode(token, {
        code,
        state,
        codeVerifier: pkceValues.codeVerifier,
      });

      clearStoredPkceValues();
      setIsLoading(false);
      setIsSuccess(true);
      onSuccess?.(); // Notify parent to refresh status

      setTimeout(() => {
        resetAndClose();
      }, 1200);
    } catch (err: unknown) {
      clearStoredPkceValues();
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(
        axiosError?.response?.data?.message ||
        (err as Error).message ||
        'OAuth exchange failed'
      );
      setIsLoading(false);
    }
  };

  const resetAndClose = () => {
    setAuthMethod('select');
    setApiKey('');
    setAuthCode('');
    setError(null);
    setIsSuccess(false);
    clearStoredPkceValues();
    onOpenChange(false);
  };

  const handleClose = () => {
    if (!isLoading && !isSuccess) {
      resetAndClose();
    }
  };

  // Success screen
  if (isSuccess) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[400px]">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3 mb-4">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-lg font-medium">Connected</p>
            <p className="text-sm text-muted-foreground">Your Claude account is ready to use</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Method selection screen
  if (authMethod === 'select') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Connect Your Claude Account</DialogTitle>
            <DialogDescription>
              Use your own Anthropic account for unlimited AI usage. You'll be billed directly by Anthropic.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 py-4">
            <button
              onClick={() => setAuthMethod('claude-pro-max')}
              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent transition-colors text-left"
            >
              <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-sm">Claude account with subscription</div>
                <div className="text-xs text-muted-foreground">Pro, Max, Team, or Enterprise</div>
              </div>
            </button>

            <button
              onClick={() => setAuthMethod('manual-api-key')}
              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent transition-colors text-left"
            >
              <Key className="h-4 w-4 text-purple-500 flex-shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-sm">Enter API Key</div>
                <div className="text-xs text-muted-foreground">Use Anthropic API key</div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Claude Pro/Max OAuth screen
  if (authMethod === 'claude-pro-max') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              Claude account with subscription
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleOAuthSubmit} className="space-y-4">
            <div className="space-y-2">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenOAuthPage();
                }}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                Open authorization page
                <ExternalLink className="h-3 w-3" />
              </a>
              <p className="text-sm text-muted-foreground">
                Sign in and paste the code you receive
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="authCode" className="text-sm">Code</Label>
              <Input
                id="authCode"
                type="text"
                placeholder="Paste your code"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                disabled={isLoading}
                autoComplete="off"
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="submit" size="sm" disabled={!authCode.trim() || isLoading}>
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // Manual API Key screen
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-4 w-4 text-purple-500" />
            Enter API Key
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleManualSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiKey" className="text-sm">API Key</Label>
            <Input
              id="apiKey"
              type="password"
              placeholder="sk-ant-api03-..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={isLoading}
              autoComplete="off"
            />
            <a
              href="https://console.anthropic.com/settings/keys"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
            >
              Get key from Anthropic Console
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" size="sm" disabled={!apiKey.trim() || isLoading}>
              {isLoading ? (
                <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Validating...</>
              ) : (
                'Connect'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
